import React, { useCallback, useEffect, useMemo, useState, Suspense } from "react";
import { useLocation, useParams } from "react-router-dom";
import { fetchData, deleteItem } from "../../../utils/apiUtils";
import { DeleteSingleButton, checkImage } from "../../../helper/ImageFormat";
import DeleteModel from "../../../Components/DeleteModel";
import Breadcrumb from "../../../helper/Breadcrumb";
import Spinner from "../../../Components/spinner";
import CustomPagination from "../../../helper/Pagination";
import ProductMultiAsyncSelect from "../components/ProductAsyncSelect";
import CatalogueAsyncSelect from "../components/CatalogueAsyncSelect";
import CollectionSelect from "../components/CollectionSelect";
import CMSSelect from "../components/CmsSelect";
import PerPageSelect from "../../../Components/PerPageSelect";
import dateformate from "../../../helper/DateFormate";
import BackButton from "../../../Components/BackButton";

const HomeItem = () => {
  const { itemId } = useParams();
  const { state: parentData } = useLocation();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const totalPages = useMemo(
    () => Math.ceil(totalCount / perPage),
    [totalCount, perPage]
  );
  const fetchPaginationData = useCallback(
    async (page = 1, searchQuery = "") => {
      setLoading(true);
      await fetchData(
        "home-section-item-pagination",
        { perPage, pageNo: page, search: searchQuery, homeSectionId: itemId },
        setData,
        setTotalCount,
        setCurrentPage,
        setLoading
      );
    },
    [perPage, itemId]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPaginationData(1);
    },  0);

    return () => clearTimeout(timer);
  }, [perPage, fetchPaginationData]);

  const selectedProducts = useMemo(() => {
    return data
      .filter((i) => i.type === "PRODUCT" && i.product)
      .map((i) => ({
        value: i.product.id,
        label: i.product.name,
        sku: i.product.sku,
        image: i.product.image,
      }));
  }, [data]);

  const selectedCatalogues = useMemo(() => {
    return data
      .filter((i) => i.type === "CATALOGUE" && i.catalogue)
      .map((i) => ({
        value: i.catalogue.id,
        label: i.catalogue.name,
        image: i.catalogue.image,
      }));
  }, [data]);

  const selectedCollections = useMemo(() => {
    return data
      .filter((i) => i.type === "COLLECTION" && i.collection)
      .map((i) => ({
        value: i.collection.id,
        label: i.collection.name,
        image: i.collection.image,
      }));
  }, [data]);

  const selectedCMS = useMemo(() => {
    return data
      .filter((i) => i.type === "CMS" && i.cms)
      .map((i) => ({
        value: i.cms.id,
        label: i.cms.title,
        image: i.cms.image,
      }));
  }, [data]);

  const handleDelete = (item) => {
    setDeleteItemId(item);
    setDeleteModalOpen(true);
  };


  const confirmDelete = async () => {
      setIsDeleting(true);
      try {
        const res = await deleteItem("home-section-item", deleteItemId?.id, fetchPaginationData);
        setDeleteItemId({});
        setDeleteModalOpen(false);
        return res;
      } catch (err) {
        throw err;
      } finally {
        setIsDeleting(false);
      }
    };

  
  return (
    <div className="content-wrapper">
      <div className="page-header mt-5 d-flex justify-content-between">
        <h3 className="custom-page-title">Home Items</h3>
        <Breadcrumb
          items={[
            { label: "Home", path: "/" },
            { label: "Home Items", path: "/home-items" },
          ]}
        />
      </div>
      <div className="d-flex justify-content-between mb-2 bg-white p-2">
         <PerPageSelect value={perPage} onChange={setPerPage}/>
        <div className="d-flex" style={{gap:"15px"}}>
          <BackButton/>
        

          {(parentData?.type === "PRODUCTS" ||
            parentData?.type === "CATALOGUE") && (
            <>
              <ProductMultiAsyncSelect
                parentId={itemId}
                value={selectedProducts}
                onChange={fetchPaginationData}
                endPoint="/home-section-item"
                type="PRODUCT"
              />

              <CatalogueAsyncSelect
                parentId={itemId}
                value={selectedCatalogues}
                onChange={fetchPaginationData}
                endPoint="/home-section-item"
                type="CATALOGUE"
              />
            </>
          )}

          {parentData?.type === "COLLECTION" && (
            <CollectionSelect
              parentId={itemId}
              value={selectedCollections}
              onChange={fetchPaginationData}
              endPoint="/home-section-item"
            />
          )}

          {parentData?.type === "CMS" && (
            <CMSSelect
              parentId={itemId}
              value={selectedCMS}
              onChange={fetchPaginationData}
              endPoint="/home-section-item"
            />
          )}
        </div>
      </div>
      <div className="card">
        <div className="card-body">
          <table  className="custom-table table table-striped">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Image</th>
                <th>Updated At</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="12" className="text-center">
                    <Spinner />
                  </td>
                </tr>
              ) : data.length ? (
                data.map((item) => {
                  const map = {
                    PRODUCT: item.product,
                    CATALOGUE: item.catalogue,
                    COLLECTION: item.collection,
                    CMS: item.cms,
                  }[item.type];

                  return (
                    <tr key={item.id}>
                      <td>{map?.name || map?.title}</td>
                      <td>{item.type}</td>
                      <td>{checkImage(map?.image)}</td>
                      <td>{dateformate(item.updatedAt)}</td>
                      {/* <td>{dateformate(item.createdAt)}</td> */}
                      <td>
                        <DeleteSingleButton
                          item={item}
                          handleDelete={handleDelete}
                          module="Home Item"
                        />
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="12" className="text-center">
                    No items found
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {data.length > 0 && (
            <CustomPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={fetchPaginationData}
              perPage={perPage}
              totalCount={totalCount}
            />
          )}

          <Suspense fallback={null}>
            <DeleteModel
              deleteModalOpen={deleteModalOpen}
              setDeleteModalOpen={() => setDeleteModalOpen(false)}
              onConfirm={confirmDelete}
              itemName="Home Item"
              isDeleting={isDeleting}
            />
          </Suspense>
        </div>
      </div>
    </div>
  );
};

export default HomeItem;
